import React, { useEffect, useRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { scrollElementIntoView } from '../../util/scrollUtils';
import { responsiveFontSize } from '../../util/responsiveUtils';

export type AlertBannerSeverity = 'warning' | 'info' | 'error' | 'success';

export interface AlertBannerProps {
  /** Message text (or use children for custom content) */
  message?: React.ReactNode;
  children?: React.ReactNode;
  severity?: AlertBannerSeverity;
  /** When true (default for severity="error"), scroll this banner into view when shown. Set to false for informational error-style content (e.g. cancellation reasons) that should not trigger scroll-on-error behavior. */
  scrollIntoView?: boolean;
  sx?: object;
}

const severityStyles = (
  theme: Theme,
  severity: AlertBannerSeverity
): { bg: string; border: string; color: string } => {
  const palette = theme.palette;
  const color =
    severity === 'warning'
      ? palette.warning.main
      : severity === 'error'
        ? palette.error.main
        : severity === 'success'
          ? palette.success.main
          : palette.info.main;
  return {
    bg: alpha(color, 0.08),
    border: alpha(color, 0.25),
    color: severity === 'warning' ? palette.warning.dark : color,
  };
};

export const AlertBanner: React.FC<AlertBannerProps> = ({
  message,
  children,
  severity = 'warning',
  scrollIntoView,
  sx = {},
}) => {
  const theme = useTheme();
  const styles = severityStyles(theme, severity);
  const boxRef = useRef<HTMLDivElement>(null);
  const hasScrolledForErrorRef = useRef(false);

  const content = children ?? message;
  const isError = severity === 'error';
  const hasContent = content != null && content !== '';
  // Only scroll for errors when scrollIntoView is not explicitly false (default true for errors)
  const shouldScrollIntoView = isError && hasContent && (scrollIntoView !== false);

  // When showing an error that should scroll, scroll it into view once so the user sees it (e.g. after a failed action)
  useEffect(() => {
    if (shouldScrollIntoView) {
      if (!hasScrolledForErrorRef.current && boxRef.current) {
        hasScrolledForErrorRef.current = true;
        requestAnimationFrame(() => {
          scrollElementIntoView(boxRef.current, { behavior: 'smooth', additionalOffset: 16 });
        });
      }
    } else {
      hasScrolledForErrorRef.current = false;
    }
  }, [shouldScrollIntoView]);

  return (
    <Box
      ref={boxRef}
      sx={{
        mb: 2,
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.5, sm: 1.25 },
        borderRadius: { xs: 1.5, sm: 2 },
        backgroundColor: styles.bg,
        border: `1px solid ${styles.border}`,
        ...sx,
      }}
    >
      {typeof content === 'string' ? (
        <Typography
          variant="body2"
          sx={{
            color: styles.color,
            fontWeight: 600,
            fontSize: responsiveFontSize.subtextFixed,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {content}
        </Typography>
      ) : (
        content
      )}
    </Box>
  );
};
